import { apiFetch } from './http';
import { setImpersonationContext, setImpersonationRefusedHandler } from './impersonationToken';

// ─────────────────────────────────────────────────────────────────────
// OPERATING AN ORGANIZATION FROM /sa (RBAC-3).
//
// The token is held in memory for the life of the tab and sent in its own
// header beside the ordinary Authorization. It is deliberately NOT persisted:
// a context is short, explicit and meant to be forgotten when the window
// closes — a copy in localStorage would outlive the intent behind it.
// ─────────────────────────────────────────────────────────────────────

export interface ImpersonationState {
  impersonationId: string;
  organization: { id: string; kind: string; name: string };
  token: string;
  expiresAt: string;
}

let current: ImpersonationState | null = null;
const listeners = new Set<(s: ImpersonationState | null) => void>();

export function currentImpersonation(): ImpersonationState | null {
  return current;
}

/**
 * The organization being impersonated HAS BEEN RENAMED (#766).
 *
 * The context is a snapshot taken when impersonation started, and its
 * `organization.name` is what the portal projects while an operator works
 * inside a customer's club. Without this, an operator renaming that club saw
 * the old name put straight back by the projection.
 *
 * Only ever for the organization the context is ALREADY for: a rename is
 * acknowledged for the acting organization, and a context pointing somewhere
 * else is not it.
 */
export function impersonationRenamed(organizationId: string, name: string): void {
  if (!current || !name) return;
  if (current.organization.id !== organizationId) return;
  current = { ...current, organization: { ...current.organization, name } };
  for (const l of [...listeners]) l(current);
}

export function subscribeImpersonation(fn: (s: ImpersonationState | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Clears the context when its window closes, without waiting for a request.
 *
 * COURTESY, NOT AUTHORITY (#438 review). The server's refusal is what actually
 * decides — this only stops a banner naming an organization the operator can
 * no longer act in from sitting there until they next click something. An
 * operator who reads it and walks away is the case it exists for.
 *
 * It is allowed to be EARLY and not allowed to be relied on: the browser
 * clock is the browser's, and a skewed one would end a context sooner or later
 * than the server does. Early is harmless — the context is gone from the
 * screen and a new one is one click away — which is why the timer may clear
 * but nothing may extend.
 */
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleExpiry(next: ImpersonationState | null) {
  if (expiryTimer !== null) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  if (!next) return;
  const remaining = new Date(next.expiresAt).getTime() - Date.now();
  // An unparseable or already-past `expiresAt` clears on the next tick rather
  // than scheduling a negative timeout or, worse, nothing at all.
  if (Number.isNaN(remaining)) return;
  expiryTimer = setTimeout(() => announce(null), Math.max(0, remaining));
}

function announce(next: ImpersonationState | null) {
  current = next;
  // The transport reads the context from the leaf module, so every request
  // made while one is active carries BOTH the token and the organization it is
  // about, without each caller remembering either.
  setImpersonationContext(
    next ? { token: next.token, organizationId: next.organization.id } : null,
  );
  scheduleExpiry(next);
  for (const fn of listeners) fn(next);
}

// THE SERVER'S ANSWER IS AUTHORITATIVE (#438 review).
//
// Registered at module load, so a refusal reaching the transport ends the
// context here — the banner disappears, the transport stops sending the token,
// and `Padel24App`'s watcher returns the operator to /sa. Exactly what
// «Avslutt» does, because it is the same state change.
//
// The IDENTITY is untouched. The person is still a signed-in StarPoint
// employee; it is the CONTEXT that expired, and ending their Entra session
// because a 45-minute window closed would be a different, wrong answer.
setImpersonationRefusedHandler(() => announce(null));

export async function startImpersonation(
  organizationId: string,
  reason: string,
): Promise<{ error: string | null }> {
  const res = await apiFetch('/platform/impersonations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ organizationId, reason }),
  });
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (!res.ok) return { error: String(body?.error ?? 'failed') };
  announce(body as unknown as ImpersonationState);
  return { error: null };
}

export async function endImpersonation(): Promise<void> {
  const active = current;
  if (!active) return;
  // THE SERVER IS TOLD FIRST, so the context is revoked and audited as ended
  // by the operator rather than left to expire (💬13 review of #1212): the
  // request leaves with the context still active locally, addressed as every
  // platform request is — to no organization — so no scope transition can
  // refuse it. Then it is cleared locally, WHATEVER the server answered: a
  // failed request still expires on its own, and continuing to send a token
  // the operator believes they have ended is the worse of the two failures.
  try {
    await apiFetch(`/platform/impersonations/${active.impersonationId}`, { method: 'DELETE' });
  } catch {
    // Expires on its own; the local clear below is what the operator asked for.
  } finally {
    announce(null);
  }
}
